import {
  sendSessionEvent,
  getSessionEventStream,
  updateSession,
  getSession
} from '@/api/new-agent/qca.js'
// import AppMessage from '@/components/message/index.js'
import { onScopeDispose, ref } from 'vue'
import dayjs from 'dayjs'

// Agent 开始思考，页面展示思考中占位。
export const QODER_THINKING_EVENT = 'agent.thinking'

// Agent 调用工具，页面按 name: query 回显调用信息。
export const QODER_TOOL_USE_EVENT = 'agent.tool_use'

// Bash 工具需要回显 description（而非原始 command）。
const BASH_TOOL_NAME = 'Bash'

// Agent 投递文件产物，页面展示可点击的文件卡片。
export const QODER_ARTIFACT_DELIVERED_EVENT = 'agent.artifact_delivered'

// sales-coaching 前缀的 markdown 投递物承载的是结构化数据，去掉前缀后按 JSON 展示。
const JSON_ARTIFACT_CONTENT_TYPE = 'text/markdown'
const JSON_ARTIFACT_FILENAME_PATTERN = /^sales-coaching[-_\s]+(.+)$/

/**
 * 识别 sales-coaching 前缀的 markdown 投递物：同级标记 isJson，并去掉文件名前缀。
 * SSE 与历史会话共用这套规则，保证两条链路的卡片表现一致。
 * @param {Object} file 文件信息，需含 contentType 与 originalFilename
 * @returns {Object} 处理后的文件信息
 */
export const markJsonArtifact = (file) => {
  if (file.contentType !== JSON_ARTIFACT_CONTENT_TYPE) return file

  const matched = JSON_ARTIFACT_FILENAME_PATTERN.exec(String(file.originalFilename || '').trim())
  if (!matched) return file

  return { ...file, originalFilename: matched[1], isJson: true }
}

// 会话级错误，页面回显 error.message。
export const QODER_ERROR_EVENT = 'session.error'

// 一条 Qoder 消息只承载一种类型的数据，qoder-message 按 blockType 选择渲染分支。
export const QODER_BLOCK_TYPES = Object.freeze({
  THINKING: 'thinking',
  MESSAGE: 'message',
  TOOL_USE: 'tool_use',
  ARTIFACT: 'artifact',
  ERROR: 'error'
})

/** 主动 abort 属于预期行为，不算请求失败。 */
const isAbortError = (error) => error?.name === 'AbortError'

/**
 * 将 ReadableStream 中以空行分隔的 SSE 数据转换为异步迭代器。
 * @param {ReadableStream} stream SSE 原始流
 * @param {AbortSignal} [signal] 用于中断读取的信号
 */
async function* sseStreamToIterator(stream, signal) {
  const decoder = new TextDecoder()
  const reader = stream.getReader()
  let buffer = ''

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) {
        // flush 解码器，处理最后一个 UTF-8 字符跨 chunk 的情况。
        buffer += decoder.decode()
        if (buffer.trim()) {
          yield processSSEMessage(buffer)
        }
        break
      }

      buffer += decoder.decode(value, { stream: true })

      while (true) {
        // 一个 SSE 消息以空行结束，剩余半包继续保留到下一次读取。
        const boundaryMatch = buffer.match(/\r?\n\r?\n/)
        if (!boundaryMatch) break

        const fullMessage = buffer.slice(0, boundaryMatch.index)
        buffer = buffer.slice(boundaryMatch.index + boundaryMatch[0].length)

        yield processSSEMessage(fullMessage)
      }
    }
  } finally {
    // 非 abort 场景（提前退出、读完）主动 cancel，避免连接悬挂占用浏览器并发额度。
    if (!signal?.aborted) {
      try {
        await reader.cancel()
      } catch (error) {
        // 流已关闭或已报错，忽略。
      }
    }
    try {
      reader.releaseLock()
    } catch (error) {
      // 同上。
    }
  }
}

/**
 * 解析单条 SSE 原始文本，合并多行 data 字段并保留标准元数据。
 */
function processSSEMessage(rawMessage) {
  const lines = rawMessage.split(/\r?\n/)
  const message = {}

  for (const line of lines) {
    if (line.startsWith('data:')) {
      // SSE 仅允许忽略 data: 后的一个可选空格，不能 trim 掉 Markdown 中的缩进和空格。
      const dataValue = line.startsWith('data: ') ? line.slice(6) : line.slice(5)
      message.data = (message.data || '') + dataValue + '\n'
    } else if (line.startsWith('event:')) {
      message.event = line.slice(6).trim()
    } else if (line.startsWith('id:')) {
      message.id = line.slice(3).trim()
    } else if (line.startsWith('retry:')) {
      message.retry = Number(line.slice(6).trim())
    }
  }

  if (message.data) {
    // SSE 多行 data 拼接后只移除协议补上的最后一个换行符。
    message.data = message.data.endsWith('\n') ? message.data.slice(0, -1) : message.data
  }

  return message
}

/**
 * 消费 Qoder Cloud Session 的 SSE 流，把每条事件解析后交给回调处理。
 * @param {ReadableStream} readableStream SSE 原始流
 * @param {{ requestId?: number, handleEvent?: Function, signal?: AbortSignal }} options
 * 请求版本号、事件处理函数、中断信号
 */
export const consumeQoderSseStream = async (
  readableStream,
  { requestId, handleEvent, signal, isActive = () => !signal?.aborted } = {}
) => {
  if (!readableStream) return

  try {
    for await (const { data, event, id } of sseStreamToIterator(readableStream, signal)) {
      // 非当前有效请求不再更新会话内容；若旧会话已结束，只通知仍挂载的 Agent 菜单刷新列表。
      // 正常路径下旧流已被 abort，这里只是 abort 前已在队列中的少量事件的兜底。
      if (!isActive(requestId)) {
        return
      }
      let payload = null
      if (data) {
        try {
          payload = JSON.parse(data)
        } catch (error) {
          payload = null
        }
      }

      handleEvent?.({
        event,
        id: id || payload?.id || '',
        data: payload,
        requestId: requestId
      })
    }
  } catch (error) {
    // 主动断开是预期行为，不打印错误。
    if (!isAbortError(error)) console.error('Qoder Cloud session SSE failed:', error)
  }
}

// 会话仍在 running 时最多自动续连 5 次：连续多次重连仍无产出说明流反复异常，
// 此时放弃续连，把输入控制权交还用户，避免无限打接口。
const MAX_AUTO_RECONNECT = 5

/**
 * Qoder Cloud Agent 会话能力。
 * @param {{ sessionId?: import('vue').Ref, messageList?: import('vue').Ref,
 * hasUnfinishedSession?: import('vue').Ref, scrollToBottom?: Function, onRefreshSessions?: Function }} options 页面状态与滚动方法
 */
// eslint-disable-next-line max-lines-per-function
export function useQoderCloudAgent({
  sessionId,
  messageList,
  hasUnfinishedSession,
  scrollToBottom,
  onRefreshSessions
} = {}) {
  let disposed = false
  // 通过递增请求版本号隔离旧 SSE：每次发起新的 SSE 流都会自增，
  // 旧版本（或已离开页面）的后续事件不再更新当前会话，避免不同轮次内容互相串写。
  // 版本号只做兜底，真正的收敛靠下面的 AbortController 物理断开流。
  let activeRequestId = Math.floor(Date.now() / 1000)

  // requestId -> { controller, received }，记录每条存活的 SSE 流，用于切会话/离开页面时断开。
  const activeSseControllers = new Map()

  /**
   * 断开其余所有 SSE 流，保证同一时刻最多只有一条连接占用浏览器并发额度。
   * @param {number} [keepRequestId] 需要保留的请求版本号，为空表示全部断开
   */
  const abortSseStreams = (keepRequestId) => {
    activeSseControllers.forEach((stream, requestId) => {
      if (requestId === keepRequestId) return
      activeSseControllers.delete(requestId)
      // 已产出过事件说明会话可能仍在后台输出，断开后补一次列表刷新，让侧边栏状态对齐。
      if (stream.received && onRefreshSessions) {
        setTimeout(() => {
          onRefreshSessions?.()
        }, 1000)
      }
      stream.controller.abort()
    })
  }

  /**
   * 生成本次请求的唯一版本号，同时让此前所有未结束的流失效（开流时会物理断开它们）。
   * @returns {number} 本次请求的 requestId
   */
  const createRequestId = () => ++activeRequestId

  /**
   * 判断该请求是否仍是当前有效请求。
   * @param {number} requestId 请求版本号
   * @returns {boolean} 实例仍有效且版本号最新时为 true
   */
  const isActiveRequest = (requestId) => requestId === activeRequestId && !disposed

  // Qoder 流式消息：当前正在输出的那一条消息，用于增量追加内容。
  const streamingQoderMessage = ref(null)
  // 当前 SSE 中各事件 id 对应的消息，确保 start/delta/buffered 事件写入同一条消息。
  const qoderEventMessages = new Map()
  // 消息自增序号，保证本轮每条消息的 key 唯一。
  let qoderMessageSeq = 0

  // 自动续连次数只在同一个会话内累计，切会话、手动发送时归零。
  let autoReconnectCount = 0
  // 当前这轮计数归属的会话 id：sessionId 变化即视为换了会话，无需依赖调用方显式重置。
  let autoReconnectSessionId = ''

  /**
   * 重置自动续连计数，并把计数归属切到指定会话。
   * @param {string} [sid] 新的归属会话 id，默认取当前会话
   */
  const resetAutoReconnect = (sid = sessionId?.value) => {
    autoReconnectCount = 0
    autoReconnectSessionId = sid || ''
  }

  /** 从 agent.message 事件中提取可渲染的文本内容。 */
  const extractQoderMessageText = (data) => {
    const contentList = Array.isArray(data?.content) ? data.content : []
    return contentList.map((item) => (typeof item?.text === 'string' ? item.text : '')).join('')
  }

  /** 结束上一条正在输出的消息，保证同一时刻只有一条消息处于流式状态。 */
  const closeStreamingMessage = () => {
    if (streamingQoderMessage.value) streamingQoderMessage.value.streaming = false
  }

  /**
   * 删除已结束的思考占位（整条从列表移除，而不是隐藏）。
   * 思考只是等待期间的提示，一旦真正的内容开始输出就失去意义；
   * 保留在列表里会把连续的工具调用隔断成两个分组，因此这里直接删掉。
   */
  const removeFinishedThinking = () => {
    const thinking = streamingQoderMessage.value
    if (thinking?.blockType !== QODER_BLOCK_TYPES.THINKING) return

    const index = messageList.value.indexOf(thinking)
    if (index >= 0) messageList.value.splice(index, 1)
    // 事件表里的引用同步清理，避免重连时把内容写进一条已不存在的消息。
    qoderEventMessages.forEach((message, eventId) => {
      if (message === thinking) qoderEventMessages.delete(eventId)
    })
    streamingQoderMessage.value = null
  }

  /**
   * 新增一条独立的 Qoder 消息：每类事件单独成条，由 qoder-message 按 blockType 渲染。
   * @param {{ blockType: string, eventId?: string, extra?: Object }} options 块类型、事件 id、附加字段
   * @returns {Object} 响应式数组中的消息对象
   */
  const pushQoderMessage = ({ blockType, eventId, extra = {} }) => {
    closeStreamingMessage()
    // 后续内容到来意味着上一条思考已经结束，先把占位删掉再入列新的内容。
    removeFinishedThinking()
    qoderMessageSeq += 1
    messageList.value.push({
      id: `${eventId || 'qoder'}-${qoderMessageSeq}`,
      type: 'qoder',
      blockType,
      text: '',
      streaming: true,
      ...extra
    })
    // 从响应式数组中取回代理对象，后续增量修改才能触发 Vue 更新。
    streamingQoderMessage.value = messageList.value[messageList.value.length - 1]
    if (eventId) qoderEventMessages.set(eventId, streamingQoderMessage.value)
    return streamingQoderMessage.value
  }

  const getBlockType = (type) => {
    if (type === QODER_THINKING_EVENT) return QODER_BLOCK_TYPES.THINKING
    if (type === 'agent.message') return QODER_BLOCK_TYPES.MESSAGE
    if (type === QODER_TOOL_USE_EVENT) return QODER_BLOCK_TYPES.TOOL_USE
    if (type === QODER_ERROR_EVENT) return QODER_BLOCK_TYPES.ERROR
    return ''
  }

  const handleEventStart = (id, data) => {
    const eventType = data?.event?.type || data?.type
    const blockType = getBlockType(eventType)
    if (!blockType || !id) return

    // TODO 重连
    // if (eventType === 'agent.message') {
    //   activeRequestId += 1 // 原请求id+1，模拟走sse续连。但不走startNewConversation，因为startNewConversation会把streamingQoderMessage重置
    //   getQoderSseEventStream({ sessionId: sessionId.value, lastEventId: id })
    // }

    const message = pushQoderMessage({ blockType, eventId: id })
    if (blockType === QODER_BLOCK_TYPES.THINKING) message.text = 'Agent 正在思考'
    scrollToBottom?.()
  }

  const appendEventDelta = (id, data) => {
    const message = qoderEventMessages.get(id)
    const text = data?.delta?.content?.text
    if (message?.blockType === QODER_BLOCK_TYPES.MESSAGE && typeof text === 'string') {
      message.text += text
      scrollToBottom?.()
    }
  }

  /** Agent 思考占位独立成条。 */
  const showQoderThinking = (eventId) => {
    if (qoderEventMessages.get(eventId)) return
    const message = pushQoderMessage({ blockType: QODER_BLOCK_TYPES.THINKING, eventId })
    message.text = 'Agent 正在思考'
    scrollToBottom?.()
  }

  /** 组装工具调用回显文本，格式为 name: query。 */
  const formatQoderToolUseText = (data) => {
    const name = data?.name
    if (!name) return ''

    const input = data?.input
    if (!input || typeof input !== 'object') return name

    // Bash 同时带 command 与 description，优先取可读的 description，与历史会话链路保持一致。
    const description =
      name === BASH_TOOL_NAME && typeof input.description === 'string'
        ? input.description.trim()
        : ''
    if (description) return description

    const query = typeof input.query === 'string' && input.query ? input.query : ''
    if (query) return query

    const fallback = Object.values(input).find((value) => typeof value === 'string' && value)
    return fallback ? fallback : name
  }

  /** 展示 Agent 工具调用信息，每次调用独立成条。 */
  const showQoderToolUse = (data, eventId) => {
    const text = formatQoderToolUseText(data)
    if (!text) return

    const currentMessage = qoderEventMessages.get(eventId)
    if (currentMessage?.blockType === QODER_BLOCK_TYPES.TOOL_USE) {
      currentMessage.text = text
      currentMessage.toolName = data?.name || ''
      scrollToBottom?.()
      return
    }

    pushQoderMessage({
      blockType: QODER_BLOCK_TYPES.TOOL_USE,
      eventId,
      extra: { text, toolName: data?.name || '', eventProcessedAt: data?.processed_at || '' }
    })
    scrollToBottom?.()
  }
  /** 取事件时间，并格式化为可读时间，解析失败时保留原值。 */
  const formatProcessedAt = (value) => {
    if (!value) return ''
    const date = dayjs(value)
    return date.isValid() ? date.format('YYYY-MM-DD HH:mm:ss') : String(value)
  }

  /** 展示 Agent 投递的文件产物，并保留点击后所需的文件信息。 */
  const showQoderArtifact = (data, eventId) => {
    const contentType = data?.content_type
    const fileId = data?.file_id
    const processedAt = formatProcessedAt(data?.processed_at)
    const originalFilename = data?.original_filename
    if (!contentType || !fileId || !originalFilename) return

    pushQoderMessage({
      blockType: QODER_BLOCK_TYPES.ARTIFACT,
      eventId,
      extra: {
        ...markJsonArtifact({ contentType, fileId, processedAt, originalFilename }),
        eventProcessedAt: data?.processed_at || ''
      }
    })
    scrollToBottom?.()
  }

  /**
   * 结束本轮 Qoder 输出，并把当前消息标记为已完成。
   * @param {requestId} requestId 有requestId 表示当前窗口就是当前激活的会话
   */
  const finishQoderMessage = async (requestId) => {
    // 本轮结束时尚未接上内容的思考占位同样没有保留价值，收尾时一并删掉。
    removeFinishedThinking()
    if (streamingQoderMessage?.value) {
      streamingQoderMessage.value.streaming = false
      streamingQoderMessage.value = null
    }
    qoderEventMessages.clear()
    if (requestId) {
      // 换了会话就不继承上一会话的续连次数：兜住所有切换入口，不依赖调用方显式重置。
      if (autoReconnectSessionId !== sessionId.value) resetAutoReconnect()
      if (autoReconnectCount >= MAX_AUTO_RECONNECT) {
        // 已达上限：不再请求接口，直接按"本轮结束"收敛，并刷新列表让侧边栏显示真实状态。
        if (hasUnfinishedSession) hasUnfinishedSession.value = false
        onRefreshSessions?.()
        return
      }
      const sessionRes = await getSession({ id: sessionId.value })
      if (!isActiveRequest(requestId)) return
      if (['running', 'rescheduling'].includes(sessionRes?.result?.status)) {
        hasUnfinishedSession.value = true
        autoReconnectCount += 1
        autoReconnectSessionId = sessionId.value
        // SSE 是长连接，不能 await：否则本函数要等流结束才返回，
        // 末尾的会话列表刷新会被一并推迟到 SSE 结束之后。
        // 旧流的失效交给 requestId + AbortController 兜底，这里无需等待结果。
        getQoderSseEventStream({
          sessionId: sessionId.value,
          lastEventId: messageList.value[messageList.value.length - 1]?.id
        })
      } else {
        if (hasUnfinishedSession) hasUnfinishedSession.value = false
        for (let index = messageList.value.length - 1; index >= 0; index -= 1) {
          const item = messageList.value[index]
          if (item.type === 'user') break
          if (item.type === 'qoder') {
            item.completedAt = item.eventProcessedAt || new Date().toISOString()
            break
          }
        }
      }
    } else {
      if (hasUnfinishedSession) hasUnfinishedSession.value = false
    }
  }

  /** 切换会话时断开当前未结束的 SSE，旧流的后续事件不再写入新会话。 */
  const startNewConversation = () => {
    abortSseStreams()
    finishQoderMessage()
    activeRequestId += 1
    // 切会话/新建会话当场归零；此时 sessionId 可能还是旧值，finishQoderMessage 里的
    // sessionId 比对会再次兜底，因此这里不必关心调用方的赋值顺序。
    resetAutoReconnect()
  }

  /** 展示会话级错误，后端重试时保留本轮状态。 */
  const showQoderError = (data, eventId) => {
    const message = data?.error?.message || '请求异常，请稍后重试'
    const currentMessage = qoderEventMessages.get(eventId)
    if (currentMessage?.blockType === QODER_BLOCK_TYPES.ERROR) currentMessage.text = message
    else pushQoderMessage({ blockType: QODER_BLOCK_TYPES.ERROR, eventId, extra: { text: message } })
    scrollToBottom?.()

    if (data?.error?.retry_status?.type !== 'retrying') finishQoderMessage()
  }

  /** Qoder SSE 事件分发。 */
  const handleQoderEvent = ({ event, id, data, requestId }) => {
    if (event === 'event_start') return handleEventStart(id, data)
    if (event === 'event_delta') return appendEventDelta(data?.event_id || id, data)
    if (event === QODER_THINKING_EVENT) {
      if (qoderEventMessages.get(id)) return
      return showQoderThinking(id)
    }
    if (event === QODER_TOOL_USE_EVENT) return showQoderToolUse(data, id)
    if (event === QODER_ARTIFACT_DELIVERED_EVENT) return showQoderArtifact(data, id)
    if (event === QODER_ERROR_EVENT) return showQoderError(data, id)
    if (event === 'agent.message') {
      const currentMessage = qoderEventMessages.get(id)
      const text = extractQoderMessageText(data)
      if (currentMessage?.blockType === QODER_BLOCK_TYPES.MESSAGE) {
        // 最终 buffered 消息与 delta 内容相同，只有在没有 delta 时才写入。
        if (!currentMessage.text) currentMessage.text = text
        currentMessage.eventProcessedAt = data?.processed_at || ''
      } else if (text) {
        pushQoderMessage({
          blockType: QODER_BLOCK_TYPES.MESSAGE,
          eventId: id,
          extra: { text, eventProcessedAt: data?.processed_at || '' }
        })
      }
      scrollToBottom?.()
      setTimeout(() => {
        onRefreshSessions?.()
      }, 1000)
      return
    }
    if (event === 'session.status_idle' && sessionId?.value) {
      if (data?.stop_reason?.type === 'requires_action') {
        getQoderSseEventStream({ sessionId: sessionId.value, lastEventId: data?.id })
      } else {
        updateSession({
          id: sessionId.value,
          isRead: 1
        })
        // 不传 keep：连本流一起断开。传 requestId 时本流会被跳过，for-await 永远不退出，
        // openQoderSseStream 的 finally 也就永远不会执行，只能靠这里手动收尾。
        abortSseStreams()
      }
      scrollToBottom?.()
    }
  }
  /**
   * 打开并消费指定会话的 SSE 流。
   */
  const openQoderSseStream = async ({ sessionId, requestId, lastEventId }) => {
    if (!isActiveRequest(requestId)) return
    // 开流前先断开其它流，同一时刻只保留本轮这一条连接。
    abortSseStreams(requestId)
    const controller = new AbortController()
    activeSseControllers.set(requestId, { controller, received: false })
    try {
      const params = {
        id: sessionId
      }
      if (lastEventId) params.lastEventId = lastEventId
      const readableStream = await getSessionEventStream(params, { signal: controller.signal })
      await consumeQoderSseStream(readableStream, {
        requestId: requestId,
        handleEvent: (event) => {
          const activeStream = activeSseControllers.get(requestId)
          if (activeStream) activeStream.received = true
          handleQoderEvent(event)
        },
        signal: controller.signal,
        isActive: isActiveRequest
      })
    } catch (error) {
      // 主动断开不算失败；旧轮次的失败也不再污染新会话。
      if (!isAbortError(error)) console.error('Qoder Cloud session SSE failed:', error)
    } finally {
      activeSseControllers.delete(requestId)
      if (isActiveRequest(requestId)) {
        console.log('在openQoderSseStream内的finally')
        // 流关闭时收敛本轮输出状态。
        finishQoderMessage(requestId)
      }
      console.log('Qoder Cloud session SSE stream closed')
    }
  }

  const sendQoderSessionMessage = async (_params) => {
    // 手动发送开启新一轮，续连次数重新计数。
    resetAutoReconnect()
    // 本轮共用一个版本号：发送前先让上一轮未结束的流失效。
    const requestId = createRequestId()
    try {
      const params = {
        id: sessionId.value,
        ..._params
      }
      const res = await sendSessionEvent(params)
      console.log('Qoder Cloud session event sent:', res)

      await openQoderSseStream({ sessionId: sessionId.value, requestId })
    } catch (error) {
      console.error('Qoder Cloud session event/SSE failed:', error)
      if (isActiveRequest(requestId)) {
        console.log('在sendQoderSessionMessage内的catch')
        finishQoderMessage(requestId)
      }
    }
  }

  const getQoderSseEventStream = async ({ sessionId, lastEventId }) => {
    // 本轮共用一个版本号：发送前先让上一轮未结束的流失效。
    const requestId = createRequestId()
    await openQoderSseStream({ sessionId, requestId, lastEventId })
  }

  // 组件卸载（切页面）时兜底断开所有流，不再依赖路由名判断页面是否还在。
  onScopeDispose(() => {
    disposed = true
    activeRequestId += 1
    abortSseStreams()
  })

  return {
    sendQoderSessionMessage,
    getQoderSseEventStream,
    startNewConversation,
    finishQoderMessage,
    streamingQoderMessage
  }
}
